import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Hotel, MapPin, Palette, Printer, Copy, Check, Sparkles, Loader2, Plus, X, Gem } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';
import { useBrand } from '../contexts/BrandContext';
import { auth } from '../services/firebase';
import { PRICING, fmt } from '../config/pricing';
import { WhiteLabelConfig, PartnerVenue } from '../types';

interface GuestStop {
  time: string;
  name: string;
  category: string;
  description: string;
  tip: string;
  estimatedCost: string;
  address: string;
}

interface GuestPlan {
  title: string;
  tagline: string;
  totalBudget: string;
  stops: GuestStop[];
}

const VIBES = ['Foodie', 'Arts & Culture', 'Romantic', 'Family', 'Adventure', 'Shopping', 'Nightlife', 'Wellness', 'Hidden Gems'];
const CREWS = ['Couple', 'Solo traveler', 'Family with kids', 'Friends', 'Business traveler'];
const BUDGETS = ['$', '$$', '$$$', '$$$$'];
const VENUE_CATEGORIES: PartnerVenue['category'][] = ['Restaurant', 'Bar', 'Spa', 'Golf', 'Experience', 'Retail', 'Other'];

// Direct Google Maps link to the actual venue — accuracy over everything
const mapsUrl = (stop: GuestStop) =>
  `https://www.google.com/maps/search/${encodeURIComponent(`${stop.name} ${stop.address || ''}`.trim())}`;

const HotelConciergePage: React.FC = () => {
  const { brand, whiteLabelConfig, updateWhiteLabel } = useBrand();
  const isSignedIn = !!auth?.currentUser;

  // ── Property profile state ──
  const [hotelName, setHotelName] = useState('');
  const [hotelAddress, setHotelAddress] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#D4AF37');
  const [logoUrl, setLogoUrl] = useState('');
  const [conciergeWelcome, setConciergeWelcome] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // ── Portfolio / partner venues (e.g. a Tavistock property recommending
  //    Nami or Chroma — sister venues under the same umbrella company) ──
  const [partnerVenues, setPartnerVenues] = useState<PartnerVenue[]>([]);
  const [preferPartners, setPreferPartners] = useState(true);
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenueCategory, setNewVenueCategory] = useState<PartnerVenue['category']>('Restaurant');
  const [newVenueUrl, setNewVenueUrl] = useState('');
  const [newVenueDescription, setNewVenueDescription] = useState('');

  // ── Guest plan generator state ──
  const [guestName, setGuestName] = useState('');
  const [crew, setCrew] = useState(CREWS[0]);
  const [vibes, setVibes] = useState<string[]>([]);
  const [budget, setBudget] = useState('$$$');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<GuestPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useSEO({
    title: 'Hotel Concierge Studio — Branded Guest Recommendations | Aura Traveler',
    description:
      'The concierge workspace for hotels: generate branded local recommendations and guest day plans anchored to your property, in your hotel\'s visual identity.',
    canonical: 'https://www.auratraveler.com/hotel-concierge',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'For Hotels', url: 'https://www.auratraveler.com/for-hotels' },
      { name: 'Concierge Studio', url: 'https://www.auratraveler.com/hotel-concierge' },
    ],
  });

  useEffect(() => {
    document.body.style.backgroundColor = '#0A0A0A';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  // Hydrate the form from any saved white-label / hotel config
  useEffect(() => {
    if (!whiteLabelConfig) return;
    setHotelName(whiteLabelConfig.hotelName || whiteLabelConfig.agencyName || '');
    setHotelAddress(whiteLabelConfig.hotelAddress || '');
    setPrimaryColor(whiteLabelConfig.primaryColor || '#D4AF37');
    setLogoUrl(whiteLabelConfig.agencyLogo || '');
    setConciergeWelcome(whiteLabelConfig.conciergeWelcome || '');
    setContactEmail(whiteLabelConfig.contactEmail || '');
    setPartnerVenues(whiteLabelConfig.partnerVenues || []);
  }, [whiteLabelConfig]);

  const addPartnerVenue = useCallback(() => {
    const name = newVenueName.trim();
    if (!name || partnerVenues.some(v => v.name.toLowerCase() === name.toLowerCase())) return;
    const url = newVenueUrl.trim();
    setPartnerVenues(prev => [...prev, {
      name,
      category: newVenueCategory,
      ...(url && /^https?:\/\//.test(url) ? { url } : {}),
      ...(newVenueDescription.trim() ? { description: newVenueDescription.trim() } : {}),
    }]);
    setNewVenueName('');
    setNewVenueUrl('');
    setNewVenueDescription('');
  }, [newVenueName, newVenueCategory, newVenueUrl, newVenueDescription, partnerVenues]);

  const removePartnerVenue = useCallback((name: string) => {
    setPartnerVenues(prev => prev.filter(v => v.name !== name));
  }, []);

  // Match a generated stop to a portfolio venue (case-insensitive containment)
  const partnerMatch = useCallback((stopName: string): PartnerVenue | undefined =>
    partnerVenues.find(v =>
      stopName.toLowerCase().includes(v.name.toLowerCase()) ||
      v.name.toLowerCase().includes(stopName.toLowerCase())
    ), [partnerVenues]);

  const saveProfile = useCallback(async () => {
    if (!hotelName.trim()) return;
    setSaveState('saving');
    try {
      const config: WhiteLabelConfig = {
        ...(whiteLabelConfig || {}),
        enabled: true,
        agencyName: hotelName.trim(),
        hotelName: hotelName.trim(),
        hotelAddress: hotelAddress.trim() || undefined,
        primaryColor: primaryColor || undefined,
        agencyLogo: logoUrl.trim() || undefined,
        conciergeWelcome: conciergeWelcome.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        partnerVenues: partnerVenues.length ? partnerVenues : undefined,
      };
      await updateWhiteLabel(config);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2500);
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3500);
    }
  }, [hotelName, hotelAddress, primaryColor, logoUrl, conciergeWelcome, contactEmail, partnerVenues, whiteLabelConfig, updateWhiteLabel]);

  const toggleVibe = (v: string) =>
    setVibes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const generate = useCallback(async () => {
    // B2B gating: the studio is a professional tool — generation requires an
    // account so usage is attributable and the Atelier upsell has a path.
    if (!auth?.currentUser) {
      setError(`Please sign in to generate guest plans. Creating an account takes 30 seconds — and the Atelier plan (${fmt(PRICING.atelier.priceMonthly)}/mo) gives your whole concierge team ${PRICING.atelier.itinerariesPerMonth} itineraries a month with full white-label branding.`);
      return;
    }
    const destination = hotelAddress.trim() || hotelName.trim();
    if (!destination) {
      setError('Add your property name and address first — recommendations are anchored to your hotel\'s neighborhood.');
      return;
    }
    setGenerating(true);
    setError(null);
    setPlan(null);
    try {
      const res = await fetch('/api/day-planner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination,
          crew,
          vibe: vibes.length ? vibes : ['Local favorites'],
          budget,
          notes: [
            `These recommendations are for a guest staying at ${hotelName || 'the hotel'}${hotelAddress ? ` (${hotelAddress})` : ''}. Keep every stop within easy reach of the property and note walking/taxi time from the hotel in each tip.`,
            preferPartners && partnerVenues.length
              ? `PORTFOLIO PRIORITY: This property belongs to a hospitality group whose own venues include: ${partnerVenues.map(v => `${v.name} (${v.category}${v.description ? ` — ${v.description}` : ''})`).join('; ')}. When one of these portfolio venues genuinely fits the guest's interests, budget, and the moment of day, RECOMMEND IT instead of an outside alternative, using its exact name, and mention in the tip that it is part of the hotel's own collection. Do not force a portfolio venue into a slot where it doesn't fit, and never invent portfolio venues beyond this list.`
              : '',
            guestName ? `Guest name: ${guestName}.` : '',
            notes,
          ].filter(Boolean).join(' '),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || `Service returned ${res.status}`);
      }
      const result = await res.json();
      if (!result?.stops?.length) throw new Error('No recommendations were generated — please try again.');
      setPlan(result);
    } catch (err: any) {
      setError(err.message || 'Something went wrong — please try again.');
    } finally {
      setGenerating(false);
    }
  }, [hotelName, hotelAddress, crew, vibes, budget, notes, guestName, preferPartners, partnerVenues]);

  const copyPlan = useCallback(async () => {
    if (!plan) return;
    const lines = [
      `${hotelName || brand.name} — ${plan.title}`,
      conciergeWelcome || plan.tagline,
      '',
      ...plan.stops.map(s => {
        const partner = partnerMatch(s.name);
        return `${s.time} — ${s.name} (${s.category})${partner ? ` — part of the ${hotelName || 'hotel'} collection` : ''}\n${s.description}\nConcierge tip: ${s.tip}\n${s.estimatedCost} · ${s.address}\n${partner?.url ? `Site: ${partner.url}\n` : ''}Map: ${mapsUrl(s)}\n`;
      }),
      contactEmail ? `Questions? Your concierge team: ${contactEmail}` : '',
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  }, [plan, hotelName, brand.name, conciergeWelcome, contactEmail, partnerMatch]);

  const inputCls = 'w-full px-4 py-3 bg-[#141414] border border-white/10 text-white font-serif text-sm outline-none focus:border-gold/60 transition-colors placeholder:text-white/25';
  const labelCls = 'font-sans text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 block';

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <SiteNav />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-16 px-4 sm:px-6 md:px-12 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/60 mb-8 block">Concierge Studio</span>
            <h1 className="font-display text-4xl sm:text-6xl text-white leading-[0.95] tracking-tighter font-bold mb-8">
              Your property. Your brand.{' '}
              <span className="font-serif italic font-medium text-gold">Your guests.</span>
            </h1>
            <p className="font-serif text-lg text-white/60 leading-relaxed max-w-2xl">
              The working desk for hotel concierge teams: generate local recommendations anchored to
              your property's neighborhood, delivered under your hotel's own branding — ready to
              print, email, or message to a guest in under a minute.
            </p>
          </div>
        </section>

        {/* 1 — Property profile & branding */}
        <section className="py-16 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-10 border border-gold/30 rounded-full flex items-center justify-center text-gold"><Palette className="w-4 h-4" /></div>
              <div>
                <h2 className="font-display text-2xl text-white tracking-tight">Property & Branding</h2>
                <p className="font-serif text-sm text-white/40 italic">Everything you generate carries your hotel's identity.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelCls} htmlFor="hc-name">Hotel name *</label>
                <input id="hc-name" className={inputCls} value={hotelName} onChange={e => setHotelName(e.target.value)} placeholder="The Grand Palais Hotel" />
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-address">Property address / neighborhood *</label>
                <input id="hc-address" className={inputCls} value={hotelAddress} onChange={e => setHotelAddress(e.target.value)} placeholder="12 Rue de Rivoli, Paris" />
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-logo">Logo URL</label>
                <input id="hc-logo" className={inputCls} value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://yourhotel.com/logo.png" />
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-color">Brand color</label>
                <div className="flex gap-3 items-center">
                  <input id="hc-color" type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="h-11 w-14 bg-[#141414] border border-white/10 cursor-pointer" />
                  <input aria-label="Brand color hex" className={inputCls} value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-welcome">Guest welcome line</label>
                <input id="hc-welcome" className={inputCls} value={conciergeWelcome} onChange={e => setConciergeWelcome(e.target.value)} placeholder="Curated for you by the Grand Palais concierge team" />
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-email">Concierge contact email</label>
                <input id="hc-email" className={inputCls} value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="concierge@yourhotel.com" />
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={saveProfile}
                disabled={!hotelName.trim() || !isSignedIn || saveState === 'saving'}
                className="px-10 py-4 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-500 rounded-full disabled:opacity-40 disabled:pointer-events-none"
              >
                {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save Property Profile'}
              </button>
              {!isSignedIn && (
                <p className="font-serif text-sm text-white/40 italic">
                  Sign in to save your branding — the generator below works without an account.
                </p>
              )}
              {saveState === 'error' && <p className="font-serif text-sm text-red-400 italic">Could not save — please try again.</p>}
            </div>
          </div>
        </section>

        {/* 2 — Portfolio & partner venues */}
        <section className="py-16 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-10 border border-gold/30 rounded-full flex items-center justify-center text-gold"><Gem className="w-4 h-4" /></div>
              <div>
                <h2 className="font-display text-2xl text-white tracking-tight">Portfolio & Partner Venues</h2>
                <p className="font-serif text-sm text-white/40 italic">Sister restaurants, bars, spas, and experiences under your hotel group.</p>
              </div>
            </div>
            <p className="font-serif text-sm text-white/45 leading-relaxed mb-8 max-w-2xl">
              If your property belongs to a larger portfolio — say, a management company that also owns
              the destination's signature sushi house and a chef-driven brasserie — list those venues here.
              The generator will feature them first whenever they genuinely fit the guest's request, and
              they'll carry your collection's badge on every guest plan.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
              <div className="md:col-span-3">
                <label className={labelCls} htmlFor="pv-name">Venue name</label>
                <input id="pv-name" className={inputCls} value={newVenueName} onChange={e => setNewVenueName(e.target.value)} placeholder="Nami" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls} htmlFor="pv-cat">Type</label>
                <select id="pv-cat" className={inputCls} value={newVenueCategory} onChange={e => setNewVenueCategory(e.target.value as PartnerVenue['category'])}>
                  {VENUE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <label className={labelCls} htmlFor="pv-url">Website (optional)</label>
                <input id="pv-url" className={inputCls} value={newVenueUrl} onChange={e => setNewVenueUrl(e.target.value)} placeholder="https://namirestaurant.com" />
              </div>
              <div className="md:col-span-3">
                <label className={labelCls} htmlFor="pv-desc">One-line note (optional)</label>
                <input id="pv-desc" className={inputCls} value={newVenueDescription} onChange={e => setNewVenueDescription(e.target.value)} placeholder="Omakase-style Japanese on the rooftop" />
              </div>
              <div className="md:col-span-1 flex items-end">
                <button
                  onClick={addPartnerVenue}
                  disabled={!newVenueName.trim()}
                  aria-label="Add venue"
                  className="w-full py-3 bg-gold text-black hover:bg-white transition-all disabled:opacity-30 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            {partnerVenues.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {partnerVenues.map(v => (
                  <span key={v.name} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 bg-gold/5 font-sans text-xs text-white/80">
                    <Gem className="w-3 h-3 text-gold" />
                    {v.name}
                    <span className="text-white/35">· {v.category}</span>
                    <button onClick={() => removePartnerVenue(v.name)} aria-label={`Remove ${v.name}`} className="text-white/40 hover:text-red-400 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={preferPartners}
                onChange={e => setPreferPartners(e.target.checked)}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="font-sans text-xs text-white/60 uppercase tracking-[0.15em]">Feature portfolio venues first in guest plans</span>
            </label>
            <p className="font-serif text-xs text-white/35 italic mt-3">
              Remember to save your property profile above to keep this list for the whole concierge team.
            </p>
          </div>
        </section>

        {/* 3 — Guest recommendation generator */}
        <section className="py-16 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-10 border border-gold/30 rounded-full flex items-center justify-center text-gold"><Sparkles className="w-4 h-4" /></div>
              <div>
                <h2 className="font-display text-2xl text-white tracking-tight">Guest Recommendations</h2>
                <p className="font-serif text-sm text-white/40 italic">Local picks within reach of your property, matched to the guest.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelCls} htmlFor="hc-guest">Guest name (optional)</label>
                <input id="hc-guest" className={inputCls} value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Mr. & Mrs. Laurent" />
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-crew">Traveling as</label>
                <select id="hc-crew" className={inputCls} value={crew} onChange={e => setCrew(e.target.value)}>
                  {CREWS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-6">
              <span className={labelCls}>Guest interests</span>
              <div className="flex flex-wrap gap-2">
                {VIBES.map(v => (
                  <button
                    key={v}
                    onClick={() => toggleVibe(v)}
                    aria-pressed={vibes.includes(v)}
                    className={`px-4 py-2 rounded-full font-sans text-[10px] uppercase tracking-[0.2em] border transition-all ${
                      vibes.includes(v) ? 'bg-gold text-black border-gold' : 'border-white/15 text-white/60 hover:border-gold/50 hover:text-gold'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <span className={labelCls}>Budget level</span>
                <div className="flex gap-2">
                  {BUDGETS.map(b => (
                    <button
                      key={b}
                      onClick={() => setBudget(b)}
                      aria-pressed={budget === b}
                      className={`flex-1 py-3 font-sans text-sm border transition-all ${
                        budget === b ? 'bg-gold text-black border-gold' : 'border-white/15 text-white/60 hover:border-gold/50'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="hc-notes">Special requests</label>
                <input id="hc-notes" className={inputCls} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anniversary dinner, vegetarian, no museums…" />
              </div>
            </div>
            <button
              onClick={generate}
              disabled={generating}
              className="px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-500 rounded-full disabled:opacity-50 inline-flex items-center gap-3"
            >
              {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Composing recommendations…</>) : 'Generate Guest Plan'}
            </button>
            {error && <p className="font-serif text-sm text-red-400 italic mt-4">{error}</p>}
          </div>
        </section>

        {/* 3 — Branded guest plan output */}
        {plan && (
          <section className="py-16 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5 print:bg-white print:text-black">
            <div className="max-w-3xl mx-auto">
              <div className="border p-8 md:p-12 bg-[#141414] print:bg-white print:border-gray-300" style={{ borderColor: `${primaryColor}40` }}>
                <div className="text-center mb-10 pb-8 border-b border-white/10 print:border-gray-200">
                  {logoUrl && <img src={logoUrl} alt={`${hotelName} logo`} className="h-12 mx-auto mb-4 object-contain" />}
                  <p className="font-sans text-[10px] uppercase tracking-[0.4em] mb-3" style={{ color: primaryColor }}>
                    {hotelName || brand.name}
                  </p>
                  <h3 className="font-display text-3xl text-white print:text-black tracking-tight mb-2">{plan.title}</h3>
                  <p className="font-serif text-sm text-white/50 print:text-gray-600 italic">
                    {conciergeWelcome || plan.tagline}
                    {guestName ? ` — prepared for ${guestName}` : ''}
                  </p>
                </div>
                <div className="space-y-8">
                  {plan.stops.map((stop, i) => {
                    const partner = partnerMatch(stop.name);
                    return (
                    <div key={i} className="flex gap-5">
                      <div className="shrink-0 w-8 h-8 rounded-full border flex items-center justify-center font-sans text-xs" style={{ borderColor: primaryColor, color: primaryColor }}>
                        {i + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                          <h4 className="font-display text-lg text-white print:text-black tracking-tight">{stop.name}</h4>
                          {partner && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-sans text-[10px] uppercase tracking-[0.15em]" style={{ borderColor: primaryColor, color: primaryColor }}>
                              <Gem className="w-2.5 h-2.5" /> {hotelName ? `${hotelName.split(' ')[0]} Collection` : 'Hotel Collection'}
                            </span>
                          )}
                          <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-white/40 print:text-gray-500">{stop.time} · {stop.category} · {stop.estimatedCost}</span>
                        </div>
                        <p className="font-serif text-sm text-white/60 print:text-gray-700 leading-relaxed mb-1.5">{stop.description}</p>
                        {stop.tip && <p className="font-serif text-sm italic mb-1.5" style={{ color: primaryColor }}>Concierge tip: {stop.tip}</p>}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <a href={mapsUrl(stop)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-gold transition-colors print:text-gray-500">
                            <MapPin className="w-3 h-3" /> {stop.address || 'View on map'}
                          </a>
                          {partner?.url && (
                            <a href={partner.url} target="_blank" rel="noopener noreferrer" className="font-sans text-[10px] uppercase tracking-[0.2em] hover:opacity-80 transition-opacity" style={{ color: primaryColor }}>
                              Visit site ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="mt-10 pt-6 border-t border-white/10 print:border-gray-200 text-center">
                  <p className="font-serif text-xs text-white/35 print:text-gray-500 italic">
                    Estimated total: {plan.totalBudget}
                    {contactEmail ? ` · Your concierge team: ${contactEmail}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-6 no-print">
                <button onClick={() => window.print()} className="px-8 py-3 border border-white/20 text-white font-sans text-[10px] uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-all rounded-full inline-flex items-center gap-2">
                  <Printer className="w-3.5 h-3.5" /> Print for Guest
                </button>
                <button onClick={copyPlan} className="px-8 py-3 border border-white/20 text-white font-sans text-[10px] uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-all rounded-full inline-flex items-center gap-2">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy for Email / WhatsApp'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Footer CTA */}
        <section className="py-20 px-4 sm:px-6 md:px-12 text-center border-t border-white/5">
          <div className="flex items-center justify-center gap-3 mb-6 text-gold/60"><Hotel className="w-5 h-5" /></div>
          <p className="font-serif text-lg text-white/50 mb-8 max-w-xl mx-auto">
            Need full multi-day branded itineraries, white-label exports, and team seats?
          </p>
          <Link to="/for-hotels" className="inline-block px-12 py-5 border border-gold/40 text-gold font-sans text-[10px] font-bold uppercase tracking-[0.4em] hover:bg-gold hover:text-black transition-all duration-700 rounded-full">
            Explore the Hotel Program
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default HotelConciergePage;
